

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface DemandState {
  id: number;
  status: string;
  attent: string;
  job: string;
  /** 1 = domaine transport (legacy), autre = service classique — plus fiable que `job` pour le type de flux */
  domaine_id?: number;
  /** Aligné sur FormateService : "transporter" | "service" */
  demande_type?: string;
  start_position_attent: string;
  prestataire_position: {
    latitude: number;
    longitude: number;
  };
  client: any;
  prestataire: any;
  proposed_provider: any;
  starting_point?: any;
  point_of_arrival?: any;
  suivi?: any;
  devis?: any[];
  meetings?: any[];
}

export interface Prestataire {
  id: number;
  lat: number;
  lng: number;
  cycle: string;
  distance: number;
  model: {
    id: number;
    first_name: string;
    image: string;
    domaines: any[]  | null;
  };
}

export interface MapConfig {
  provider: string;
  layer: string;
  layerKey: string;
  modeSatellite: boolean;
}

export interface RouteInfo {
  distance: { Km: number; M: number };
  hours: number;
  minutes: number;
  waypointsHours?: number;
  waypointsMinutes?: number;
}

export interface ServicesAroundState {
  demand: DemandState | null;
  prestataires: Prestataire[];
  mapConfig: MapConfig;
  routeInfo: RouteInfo;
  markersList: any[];
  center: { lat: number; lng: number } | null;
  transportPoints: any[];
  isModalOpen: boolean;
  isPrestataireModalOpen: boolean;
  selectedPrestataire: any;
  arrived: boolean;
  showAlert: boolean;
  alertText: string;
  unreadConversations: number;
}

const initialState: ServicesAroundState = {
  demand: null,
  prestataires: [],
  mapConfig: {
    provider: '',
    layer: '',
    layerKey: '',
    modeSatellite: false
  },
  routeInfo: {
    distance: { Km: 0, M: 0 },
    hours: 0,
    minutes: 0
  },
  markersList: [],
  center: null,
  transportPoints: [],
  isModalOpen: false,
  isPrestataireModalOpen: false,
  selectedPrestataire: null,
  arrived: false,
  showAlert: false,
  alertText: '',
  unreadConversations: 0
};

@Injectable({
  providedIn: 'root'
})
export class ServicesAroundStateService {
  private _state$ = new BehaviorSubject<ServicesAroundState>(initialState);

  get state(): ServicesAroundState {
    return this._state$.value;
  }

  get state$(): Observable<ServicesAroundState> {
    return this._state$.asObservable();
  }

  // Actions pour mettre à jour l'état
  updateDemand(demand: DemandState | null): void {
    this.updateState({ demand });
  }

  updatePrestataires(prestataires: Prestataire[]): void {
    this.updateState({ prestataires });
  }

  updateMapConfig(mapConfig: Partial<MapConfig>): void {
    this.updateState({ 
      mapConfig: { ...this.state.mapConfig, ...mapConfig } 
    });
  }

  updateRouteInfo(routeInfo: Partial<RouteInfo>): void {
    this.updateState({ 
      routeInfo: { ...this.state.routeInfo, ...routeInfo } 
    });
  }

  updateMarkersList(markersList: any[]): void {
    this.updateState({ markersList });
  }

  updateCenter(center: { lat: number; lng: number }): void {
    this.updateState({ center });
  }

  updateTransportPoints(transportPoints: any[]): void {
    this.updateState({ transportPoints });
  }

  setModalOpen(isOpen: boolean): void {
    this.updateState({ isModalOpen: isOpen });
  }

  setPrestataireModalOpen(isOpen: boolean): void {
    this.updateState({ isPrestataireModalOpen: isOpen });
  }

  setSelectedPrestataire(prestataire: any): void {
    this.updateState({ selectedPrestataire: prestataire });
  }

  setArrived(arrived: boolean): void {
    this.updateState({ arrived });
  }

  setShowAlert(show: boolean): void {
    this.updateState({ showAlert: show });
  }

  setAlertText(text: string): void {
    this.updateState({ alertText: text });
  }

  setUnreadConversations(count: number): void {
    this.updateState({ unreadConversations: count });
  }

  resetState(): void {
    this._state$.next(initialState);
  }

  closeAllModals(): void {
    this.updateState({ 
      isModalOpen: false,
      isPrestataireModalOpen: false,
      arrived: false,
      showAlert: false,
      alertText: ''
    });
  }

  private updateState(partialState: Partial<ServicesAroundState>): void {
    this._state$.next({ ...this.state, ...partialState });
  }
} 